import React from 'react'
import { Button, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import GoogleIcon from '@mui/icons-material/Google'
import MicrosoftIcon from '@mui/icons-material/Microsoft'

interface SsoProviderButtonsProps {
    onSelectGoogle: () => void
    onSelectMicrosoft: () => void
    disabled?: boolean
    // "Sign in"/"Sign up" differ by two letters and share their first word — easy to misread
    // at a glance, which is exactly how this got confusing in the first place. "log_in" (not
    // "sign_in") shares no root with "sign_up", so the two label sets stay distinguishable
    // even at a skim. See MD-1576.
    mode: 'sign_in' | 'sign_up'
}

// Shared visual home for the Google/Microsoft entry points: Login (via GoogleMicrosoftAuth)
// and Signup both need this exact pair of buttons, and previously styled them separately —
// which is how their spacing drifted apart. One component, one style, used by both.
const SsoProviderButtons: React.FC<SsoProviderButtonsProps> = ({
    onSelectGoogle,
    onSelectMicrosoft,
    disabled,
    mode,
}) => {
    const { t } = useTranslation()
    const googleLabel = mode === 'sign_up' ? t('auth.sign_up_with_google') : t('auth.log_in_with_google')
    const microsoftLabel = mode === 'sign_up' ? t('auth.sign_up_with_microsoft') : t('auth.log_in_with_microsoft')

    return (
        // No explicit spacing: inherits the theme's MuiStack default (24px, see theme.ts) —
        // the same gap Login's Stack always got before this component existed. Hardcoding a
        // value here would silently override that default instead of just matching it.
        <Stack>
            <Button
                type="button"
                variant="contained"
                startIcon={<GoogleIcon />}
                color="primary"
                onClick={onSelectGoogle}
                disabled={disabled}
                fullWidth
            >
                {googleLabel}
            </Button>
            <Button
                type="button"
                variant="contained"
                startIcon={<MicrosoftIcon />}
                color="primary"
                onClick={onSelectMicrosoft}
                disabled={disabled}
                fullWidth
            >
                {microsoftLabel}
            </Button>
        </Stack>
    )
}

export default SsoProviderButtons
